import { useState, useEffect, useCallback } from 'react';
import { Card, Button } from 'react-bootstrap';
import { getTokenData, getDataFromIpfsGateway } from '../../services/tokenTiger';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
const GATEWAY = process.env.REACT_APP_IPFS_GATEWAY

export function TokenCard({ tokenId, appData }) {
  const navigate = useNavigate();
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isDownloadHovered, setIsDownloadHovered] = useState(false);
  const [loadingTokenData, setLoadingTokenData] = useState(false);
  const [loadedTokenData, setLoadedTokenData] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [ enableDownload , setEnableDownload ] = useState(false)

  const getCidFromUrl = useCallback((url) => {
    if (!url) return null
    const cid = url.split('/').pop()
    return cid
  }, [])



  const onClick = (tokenData) => {
    appData.updateSelectedToken(tokenData);
    navigate(`/token/${tokenId}`);
  }

  const download = async () => {
    try {
      const { mutableData } = tokenData
      if(!mutableData?.payloadCid ) {
        throw new Error('Token is not public')
      }

      window.open(`${GATEWAY}/ipfs/download/${mutableData.payloadCid}`)

    } catch (error) {
      console.warn(error)
    }
  }
  useEffect(() => {
    const fetchTokenData = async () => {
      // Review cache data
      if (appData.tokensData[tokenId]) {
        const tokenData = appData.tokensData[tokenId]
        setTokenData(tokenData)
        setLoadingTokenData(false)
        setLoadedTokenData(true)

      if(tokenData && tokenData.mutableData && tokenData.mutableData.payloadCid){
        setEnableDownload(true)
      }

        return
      }
      setLoadingTokenData(true)
      const tokenData = await getTokenData([tokenId])
      const mutableDataUrl = getCidFromUrl(tokenData[0].mutableData)
      //const immutableDataUrl = getCidFromUrl(tokenData[0].immutableData)

      const mutableData = await getDataFromIpfsGateway(`${mutableDataUrl}/data.json`)
      //const immutableData = await getDataFromIpfsGateway(`${immutableDataUrl}/data.json`)


      if(mutableData && mutableData.payloadCid){
        setEnableDownload(true)
      }


      let markdown = 'No Info Available'
      let media = null;

      try {
        const userData = JSON.parse(mutableData.userData)
        markdown = userData.markdown || 'No Info Available'

        // Extract media property from userData.media
        // Media is an array of objects with {filename, url, description}
        if (userData.media && Array.isArray(userData.media)) {
          // Extract URLs from media objects, filtering out invalid entries
          media = userData.media
            .map(item => {
              // Handle both object format {url: "...", ...} and direct string URLs
              if (typeof item === 'string') {
                return item;
              } else if (item && typeof item === 'object' && item.url) {
                return item.url;
              }
              return null;
            })
            .filter(url => url && typeof url === 'string');

          // If no valid URLs found, set to null
          if (media.length === 0) {
            media = null;
          }
        }
      } catch (error) {
        console.error(error)
      }

      const data = {
        tokenIcon: mutableData.tokenIcon,
        about: mutableData.about || 'A unique digital collectible with distinctive traits and rarity.',
        truncatedAbout: mutableData.about.length > 80 ? mutableData.about.substring(0, 80) + '...' : mutableData.about,
        name: tokenData[0]?.genesisData?.name || 'Untitled',
        tokenId: tokenId,
        markdown,
        media: media && media.length > 0 ? media : null,
        mutableData
      }

      setTokenData(data)
      // cache the token data
      appData.setTokensData({ ...appData.tokensData, [tokenId]: data })
      setLoadingTokenData(false)
      setLoadedTokenData(true)
    }
    if (!loadingTokenData && !loadedTokenData) {
      fetchTokenData()
    }
  }, [tokenId, getCidFromUrl, appData, loadedTokenData, loadingTokenData])

  if (loadingTokenData || !tokenData) {
    return (
      <Card
        style={{
          border: '1px solid var(--theme-border, #e9ecef)',
          borderRadius: 16,
          overflow: 'hidden',
          background: 'var(--theme-surface, #ffffff)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
          position: 'relative',
          transition: 'box-shadow 0.3s ease',
          width: '100%',
          height: 'clamp(400px, 60vw, 520px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Loading spinner */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Card>
    )
  }


  return (
    <>
      <Card
        style={{
          border: '1px solid var(--theme-border, #e9ecef)',
          borderRadius: 16,
          overflow: 'hidden',
          background: 'var(--theme-surface, #ffffff)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
          position: 'relative',
          transition: 'box-shadow 0.3s ease',
          width: '100%',
          height: 'clamp(400px, 60vw, 520px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            width: '100%',
            height: 'clamp(200px, 40vw, 280px)',
            overflow: 'hidden',
            background: 'var(--theme-surface, #f8f9fa)',
          }}
        >
          {<Card.Img
            src={tokenData.tokenIcon}
            alt={tokenData.name}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />}
        </div>

        <Card.Body
          style={{
            padding: 'clamp(12px, 2vw, 16px)',
            background: 'var(--theme-surface, #ffffff)',
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Name */}
          <Card.Title
            as="h3"
            style={{
              fontSize: 'clamp(16px, 3vw, 18px)',
              fontWeight: 800,
              marginBottom: 'clamp(8px, 1.5vw, 12px)',
              color: 'var(--theme-text, #212529)',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tokenData.name}
          </Card.Title>

          {/* About Text */}
          <Card.Text
            as="div"
            style={{
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'var(--theme-textSecondary, #6c757d)',
              marginBottom: 'auto',
              minHeight: 'clamp(50px, 8vw, 60px)',
              flex: 1,
            }}
          >
            <span>{tokenData.truncatedAbout}</span>
          </Card.Text>

          {/* Button Container */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(8px, 1.5vw, 10px)',
              marginTop: 'clamp(8px, 1.5vw, 12px)',
            }}
          >
            {/* See More Button */}
            <Button
              variant="primary"
              size="sm"
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              style={{
                width: '100%',
                borderRadius: 12,
                padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 24px)',
                fontWeight: 600,
                fontSize: 'clamp(13px, 2.5vw, 14px)',
                border: 'none',
                background: isButtonHovered ? 'var(--theme-primary)' : 'var(--theme-secondary)',
                color: '#ffffff',
                transform: isButtonHovered ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
                transition: 'all 0.2s ease',
                minHeight: '44px',
              }}
              onClick={() => onClick(tokenData)}
            >
              See more
            </Button>

            {/* Download Button */}
            <Button
              variant="outline"
              size="sm"
              onMouseEnter={() => setIsDownloadHovered(true)}
              onMouseLeave={() => setIsDownloadHovered(false)}
              style={{
                width: '100%',
                borderRadius: 12,
                padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 24px)',
                fontWeight: 600,
                fontSize: 'clamp(13px, 2.5vw, 14px)',
                border: enableDownload 
                  ? `2px solid var(--theme-primary, #007bff)` 
                  : `2px solid var(--theme-border, #dee2e6)`,
                background: enableDownload
                  ? isDownloadHovered
                    ? 'var(--theme-primary, #007bff)'
                    : 'transparent'
                  : 'var(--theme-disabled, #e9ecef)',
                color: enableDownload
                  ? isDownloadHovered
                    ? '#ffffff'
                    : 'var(--theme-primary, #007bff)'
                  : 'var(--theme-textMuted, #adb5bd)',
                cursor: enableDownload ? 'pointer' : 'not-allowed',
                transform: enableDownload && isDownloadHovered 
                  ? 'translateY(-2px) scale(1.02)' 
                  : 'translateY(0) scale(1)',
                transition: 'all 0.2s ease',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(6px, 1.5vw, 8px)',
                opacity: enableDownload ? 1 : 0.6,
              }}
              onClick={download}
              disabled={!enableDownload}
            >
              <Download 
                size={18} 
                style={{
                  flexShrink: 0,
                }}
              />
              <span>Download</span>
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
